'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  MapPin, Phone, MessageCircle, Car, Clock, CreditCard,
  Camera, CheckCircle, XCircle, AlertTriangle, Navigation,
  ChevronDown, ChevronUp, Play, Check, AlertCircle,
  Truck, Calendar, User
} from 'lucide-react';

interface Job {
  id: string;
  customer: string;
  phone: string;
  whatsapp: string;
  address: string;
  mapsUrl: string;
  service: string;
  vehiclePlate: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleColor: string;
  vehicleSize: string;
  paymentStatus: string;
  paymentMethod: string;
  price: number;
  time: string;
  type: 'mobile' | 'location';
  status: string;
  managerNotes: string;
  day: 'today' | 'tomorrow';
}

const jobs: Job[] = [
  {
    id: 'job-0425',
    customer: 'Shanice Paulina',
    phone: '+599 9 512 3456',
    whatsapp: '+599 9 512 3456',
    address: 'Kaya Grandi 45, Willemstad, Curaçao',
    mapsUrl: 'https://maps.google.com/?q=Kaya+Grandi+45+Willemstad+Curacao',
    service: 'Outside + Inside Wash',
    vehiclePlate: 'GHI-9012',
    vehicleBrand: 'Honda',
    vehicleModel: 'Civic',
    vehicleColor: 'White',
    vehicleSize: 'Medium',
    paymentStatus: 'pending',
    paymentMethod: 'Sentoo',
    price: 50,
    time: '09:30',
    type: 'mobile',
    status: 'inprogress',
    managerNotes: 'Customer has a dog. Be careful with pet hair on seats.',
    day: 'today',
  },
  {
    id: 'job-0423',
    customer: 'Lisette van Dijk',
    phone: '+599 9 687 9012',
    whatsapp: '+599 9 687 9012',
    address: 'Rooi Catootje 8, Curaçao',
    mapsUrl: 'https://maps.google.com/?q=Rooi+Catootje+Curacao',
    service: 'Outside Wash Only',
    vehiclePlate: 'MNO-7890',
    vehicleBrand: 'Hyundai',
    vehicleModel: 'Tucson',
    vehicleColor: 'Silver',
    vehicleSize: 'Large / SUV',
    paymentStatus: 'paid',
    paymentMethod: 'Card',
    price: 30,
    time: '12:15',
    type: 'mobile',
    status: 'assigned',
    managerNotes: 'Customer prefers driver parks on the right side of driveway.',
    day: 'today',
  },
  {
    id: 'job-0421',
    customer: 'Keisha Martina',
    phone: '+599 9 743 5678',
    whatsapp: '+599 9 743 5678',
    address: 'Kaya Flamboyan 12, Willemstad, Curaçao',
    mapsUrl: 'https://maps.google.com/?q=Kaya+Flamboyan+12+Willemstad+Curacao',
    service: 'Outside + Inside Wash',
    vehiclePlate: 'ABC-1234',
    vehicleBrand: 'Toyota',
    vehicleModel: 'Corolla',
    vehicleColor: 'Silver',
    vehicleSize: 'Medium',
    paymentStatus: 'unpaid',
    paymentMethod: 'Cash',
    price: 50,
    time: '14:30',
    type: 'mobile',
    status: 'confirmed',
    managerNotes: '',
    day: 'today',
  },
  {
    id: 'job-0430',
    customer: 'Renaldo Cijntje',
    phone: '+599 9 821 4567',
    whatsapp: '+599 9 821 4567',
    address: 'Rooi Santu Location — Bay 2',
    mapsUrl: 'https://maps.google.com/?q=Rooi+Santu+Curacao',
    service: 'Full Interior Detail',
    vehiclePlate: 'JKL-3456',
    vehicleBrand: 'Toyota',
    vehicleModel: 'Prado',
    vehicleColor: 'Black',
    vehicleSize: 'Large / SUV',
    paymentStatus: 'paid',
    paymentMethod: 'Online Banking',
    price: 125,
    time: '09:00',
    type: 'location',
    status: 'new',
    managerNotes: 'Full detail package. Estimated 3 hours. Check all seat crevices.',
    day: 'tomorrow',
  },
  {
    id: 'job-0431',
    customer: 'Mikhail Albertus',
    phone: '+599 9 654 3210',
    whatsapp: '+599 9 654 3210',
    address: 'Seru Fortuna 22, Curaçao',
    mapsUrl: 'https://maps.google.com/?q=Seru+Fortuna+Curacao',
    service: 'Wax Protection',
    vehiclePlate: 'PQR-1234',
    vehicleBrand: 'Nissan',
    vehicleModel: 'X-Trail',
    vehicleColor: 'Dark Blue',
    vehicleSize: 'Large / SUV',
    paymentStatus: 'pending',
    paymentMethod: 'Sentoo',
    price: 75,
    time: '11:00',
    type: 'mobile',
    status: 'assigned',
    managerNotes: 'Customer requested extra attention to hood and roof.',
    day: 'tomorrow',
  },
];

const statusConfig: Record<string, { label: string; cls: string; next?: string; nextLabel?: string }> = {
  new: { label: 'New', cls: 'badge-new', next: 'assigned', nextLabel: 'Accept Job' },
  assigned: { label: 'Assigned', cls: 'badge-assigned', next: 'onway', nextLabel: 'Start Journey' },
  confirmed: { label: 'Confirmed', cls: 'badge-confirmed', next: 'onway', nextLabel: 'On the Way' },
  onway: { label: 'On the Way', cls: 'badge-onway', next: 'inprogress', nextLabel: 'Start Job' },
  inprogress: { label: 'In Progress', cls: 'badge-inprogress', next: 'completed', nextLabel: 'Complete Job' },
  completed: { label: 'Completed', cls: 'badge-completed' },
  cancelled: { label: 'Cancelled', cls: 'badge-cancelled' },
  noshow: { label: 'No-show', cls: 'badge-noshow' },
};

function JobCard({ job }: { job: Job }) {
  const [expanded, setExpanded] = useState(job.status === 'inprogress');
  const [status, setStatus] = useState(job.status);
  const [beforePhoto, setBeforePhoto] = useState<string | null>(null);
  const [afterPhoto, setAfterPhoto] = useState<string | null>(null);

  const cfg = statusConfig[status] || statusConfig.new;

  const advanceStatus = () => {
    if (cfg.next) {
      setStatus(cfg.next);
      toast.success(`Job ${job.id} status updated to: ${statusConfig[cfg.next]?.label}`);
      // Backend integration point: PATCH /api/appointments/:id/status
    }
  };

  const markNoShow = () => {
    setStatus('noshow');
    toast.warning(`No-show reported for ${job.customer}`);
  };

  const payBadge = job.paymentStatus === 'paid' ? 'badge-paid' :
    job.paymentStatus === 'pending' ? 'badge-pending' : 'badge-unpaid';

  return (
    <div className={`bg-white rounded-2xl border shadow-card overflow-hidden transition-all duration-200 ${
      status === 'inprogress' ? 'border-amber-200 shadow-amber-100' :
      status === 'completed' ? 'border-green-200' :
      status === 'noshow'? 'border-gray-200 opacity-70' : 'border-sky-100'
    }`}>
      {/* Card header */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-sky-50/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          job.type === 'mobile' ? 'bg-emerald-100' : 'bg-sky-100'
        }`}>
          {job.type === 'mobile' ? <Truck size={20} className="text-emerald-600" /> : <MapPin size={20} className="text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-extrabold text-dark">{job.customer}</p>
            <span className={`badge ${cfg.cls}`}>{cfg.label}</span>
            <span className={`badge ${payBadge}`}>{job.paymentStatus}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{job.service} · {job.time} · {job.vehicleBrand} {job.vehicleModel}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-extrabold text-primary tabular-nums text-sm">{job.price} XCG</span>
          {expanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-sky-50 pt-4 space-y-5">
          {/* Info grid */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Customer</h4>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <User size={14} className="text-primary flex-shrink-0" />
                {job.customer}
              </div>
              <div className="flex items-center gap-2">
                <a href={`tel:${job.phone}`} className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <Phone size={14} />
                  {job.phone}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <a href={`https://wa.me/${job.whatsapp.replace(/\s+/g, '').replace('+', '')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-green-600 hover:underline">
                  <MessageCircle size={14} />
                  WhatsApp
                </a>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Vehicle</h4>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Car size={14} className="text-primary flex-shrink-0" />
                {job.vehicleBrand} {job.vehicleModel} — {job.vehicleColor}
              </div>
              <div className="text-sm font-mono text-slate-600 bg-sky-50 px-3 py-1.5 rounded-lg inline-block">
                {job.vehiclePlate}
              </div>
              <div className="text-xs text-muted-foreground">{job.vehicleSize}</div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Location</h4>
              <div className="flex items-start gap-2 text-sm text-slate-700">
                <MapPin size={14} className="text-primary flex-shrink-0 mt-0.5" />
                <span>{job.address}</span>
              </div>
              <a
                href={job.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-100 text-primary text-xs font-semibold hover:bg-sky-200 transition-colors"
              >
                <Navigation size={12} />
                Open in Google Maps
              </a>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide">Payment</h4>
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <CreditCard size={14} className="text-primary" />
                {job.paymentMethod}
              </div>
              <span className={`badge ${payBadge}`}>{job.paymentStatus}</span>
              <p className="font-extrabold text-primary tabular-nums">{job.price} XCG</p>
            </div>
          </div>

          {/* Manager notes */}
          {job.managerNotes && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={14} className="text-amber-600" />
                <span className="text-xs font-bold text-amber-700">Manager Notes</span>
              </div>
              <p className="text-sm text-amber-800">{job.managerNotes}</p>
            </div>
          )}

          {/* Photo uploads */}
          {status !== 'completed' && status !== 'noshow' && (
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { label: 'Before Photo', key: 'before', state: beforePhoto, setter: setBeforePhoto, color: 'sky' },
                { label: 'After Photo', key: 'after', state: afterPhoto, setter: setAfterPhoto, color: 'green' },
                { label: 'Damage Photo', key: 'damage', state: null, setter: () => {}, color: 'red' },
              ].map((photo) => (
                <div key={`photo-${job.id}-${photo.key}`}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{photo.label}</label>
                  <div
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      photo.state
                        ? 'border-green-300 bg-green-50'
                        : `border-${photo.color}-200 hover:border-${photo.color}-300 bg-${photo.color}-50/30`
                    }`}
                    onClick={() => {
                      // Backend integration point: upload photo to storage
                      if (photo.key !== 'damage') {
                        (photo.setter as React.Dispatch<React.SetStateAction<string | null>>)('uploaded');
                        toast.success(`${photo.label} uploaded`);
                      } else {
                        toast.info('Damage photo upload coming soon');
                      }
                    }}
                  >
                    {photo.state ? (
                      <div className="flex flex-col items-center gap-1">
                        <CheckCircle size={20} className="text-green-500" />
                        <span className="text-xs font-semibold text-green-700">Uploaded</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Camera size={20} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Tap to upload</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          {status !== 'completed' && status !== 'noshow' && status !== 'cancelled' && (
            <div className="flex flex-wrap gap-3 pt-2">
              {cfg.next && (
                <button
                  onClick={advanceStatus}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white transition-all duration-150 active:scale-95 ${
                    cfg.next === 'completed'
                      ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-200'
                      : cfg.next === 'inprogress' ?'bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-200' :'bg-gradient-primary shadow-glow'
                  }`}
                >
                  {cfg.next === 'completed' ? <Check size={16} /> :
                   cfg.next === 'inprogress' ? <Play size={16} /> :
                   <Navigation size={16} />}
                  {cfg.nextLabel}
                </button>
              )}
              {(status === 'assigned' || status === 'confirmed' || status === 'onway') && (
                <button
                  onClick={markNoShow}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm border-2 border-red-200 text-red-600 hover:bg-red-50 transition-all"
                >
                  <XCircle size={16} />
                  Report No-show
                </button>
              )}
              <a
                href={`https://wa.me/${job.whatsapp.replace(/\s+/g, '').replace('+', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-green-500 hover:bg-green-600 text-white transition-all"
              >
                <MessageCircle size={16} />
                Contact Customer
              </a>
            </div>
          )}

          {status === 'completed' && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
              <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-green-800">Job Completed</p>
                <p className="text-xs text-green-700">Great work! This job has been marked as done.</p>
              </div>
            </div>
          )}

          {status === 'noshow' && (
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
              <AlertTriangle size={20} className="text-gray-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-gray-700">No-show Reported</p>
                <p className="text-xs text-gray-600">Manager has been notified. Customer will be contacted.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmployeePortalContent() {
  const [dayFilter, setDayFilter] = useState<'today' | 'tomorrow'>('today');

  const todayJobs = jobs.filter((j) => j.day === 'today');
  const tomorrowJobs = jobs.filter((j) => j.day === 'tomorrow');
  const displayJobs = dayFilter === 'today' ? todayJobs : tomorrowJobs;

  const completedCount = todayJobs.filter((j) => j.status === 'completed').length;
  const inProgressJob = todayJobs.find((j) => j.status === 'inprogress');

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-dark">My Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">Jayden Rosario · Mobile Crew · Wed 30 Apr 2026</p>
        </div>
        <div className="flex items-center gap-2 bg-sky-50 rounded-2xl p-1">
          <button
            onClick={() => setDayFilter('today')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              dayFilter === 'today' ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground'
            }`}
          >
            <Calendar size={14} />
            Today ({todayJobs.length})
          </button>
          <button
            onClick={() => setDayFilter('tomorrow')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              dayFilter === 'tomorrow' ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground'
            }`}
          >
            <Calendar size={14} />
            Tomorrow ({tomorrowJobs.length})
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Total Today</p>
          <p className="text-2xl font-extrabold text-dark tabular-nums">{todayJobs.length}</p>
          <p className="text-xs text-muted-foreground">assigned jobs</p>
        </div>
        <div className="stat-card">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Completed</p>
          <p className="text-2xl font-extrabold text-green-600 tabular-nums">{completedCount}</p>
          <p className="text-xs text-muted-foreground">of {todayJobs.length} jobs</p>
        </div>
        <div className="stat-card border-amber-100">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">In Progress</p>
          <p className="text-2xl font-extrabold text-amber-600 tabular-nums">
            {todayJobs.filter((j) => j.status === 'inprogress').length}
          </p>
          <p className="text-xs text-muted-foreground">{inProgressJob ? inProgressJob.customer : 'None active'}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Revenue</p>
          <p className="text-2xl font-extrabold text-primary tabular-nums">
            {todayJobs.reduce((s, j) => s + j.price, 0)} XCG
          </p>
          <p className="text-xs text-muted-foreground">today&apos;s total</p>
        </div>
      </div>

      {/* Active job alert */}
      {inProgressJob && dayFilter === 'today' && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-6 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
            <Play size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-amber-800">Job In Progress</p>
            <p className="text-sm text-amber-700">{inProgressJob.service} for {inProgressJob.customer} · {inProgressJob.vehicleBrand} {inProgressJob.vehicleModel}</p>
          </div>
          <span className="badge badge-inprogress">Active</span>
        </div>
      )}

      {/* Jobs list */}
      {displayJobs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-sky-100">
          <Calendar size={40} className="text-sky-200 mx-auto mb-3" />
          <p className="font-bold text-dark">No jobs scheduled for {dayFilter}</p>
          <p className="text-sm text-muted-foreground mt-1">Check back later or contact your manager</p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayJobs.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-sky-100 flex items-center justify-around px-2 py-2 z-40">
        {[
          { label: 'Today', icon: Calendar, filter: 'today' as const },
          { label: 'Tomorrow', icon: Clock, filter: 'tomorrow' as const },
          { label: 'Profile', icon: User, filter: null },
        ].map((item) => (
          <button
            key={`emp-bottom-${item.label}`}
            onClick={() => { if (item.filter) setDayFilter(item.filter); }}
            className={`flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-colors ${
              dayFilter === item.filter ? 'text-primary' : 'text-muted-foreground hover:text-primary'
            }`}
          >
            <item.icon size={20} />
            <span className="text-xs font-semibold">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}